import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Trash2, GripVertical, ArrowUpToLine } from 'lucide-react';
import { PagesDTO } from '../../services/apiClient';

export interface PageTreeNode extends PagesDTO {
  children: PageTreeNode[];
}

interface PagesTreeViewProps {
  data: PagesDTO[];
  selectedId?: string | null;
  onSelect: (row: PagesDTO) => void;
  onDelete: (row: PagesDTO) => void;
  // Re-parent a page via drag-and-drop. newParentId null moves it to the top level.
  onMove?: (nodeId: string, newParentId: string | null) => void;
  // Force every node open regardless of the collapse state — used while searching so
  // matches deeper in the hierarchy aren't hidden behind collapsed ancestors.
  expandAll?: boolean;
  isLoading?: boolean;
}

const normId = (g?: string | null) => (g || '').toLowerCase();

// Build a hierarchy from the flat list using ParentPageId, ordered by Position then Title.
export const buildPageTree = (items: PagesDTO[]): PageTreeNode[] => {
  const byId = new Map<string, PageTreeNode>();
  items.forEach((i) => byId.set(normId(i.id), { ...i, children: [] }));

  const roots: PageTreeNode[] = [];
  byId.forEach((node) => {
    const parentId = normId(node.parentPageId);
    const parent = parentId ? byId.get(parentId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (nodes: PageTreeNode[]) => {
    nodes.sort(
      (a, b) =>
        (a.position ?? 0) - (b.position ?? 0) ||
        (a.title || '').localeCompare(b.title || '')
    );
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);

  return roots;
};

// Drag state + handlers shared with every row via context-like prop drilling.
interface DndState {
  dragId: string | null;
  draggedParentId: string | null; // current parent of the dragged node (dropping here is a no-op)
  overId: string | null;
  invalidTargets: Set<string>; // dragged node + its descendants — can't drop into these
  onDragStart: (id: string) => void;
  onDragEnterNode: (id: string) => void;
  onDropNode: (id: string) => void;
  onDragEnd: () => void;
}

const NodeRow: React.FC<{
  node: PageTreeNode;
  depth: number;
  selectedId?: string | null;
  onSelect: (row: PagesDTO) => void;
  onDelete: (row: PagesDTO) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  expandAll?: boolean;
  dnd?: DndState;
}> = ({
  node,
  depth,
  selectedId,
  onSelect,
  onDelete,
  expandedIds,
  onToggleExpand,
  expandAll,
  dnd,
}) => {
  const hasChildren = node.children.length > 0;
  const isSelected = normId(node.id) === normId(selectedId);

  const id = normId(node.id);
  const expanded = !!expandAll || expandedIds.has(id);
  const isDragging = dnd?.dragId === id;
  // A valid drop target = a real drag is happening, this isn't the dragged subtree,
  // and this isn't already the dragged node's current parent (which would be a no-op).
  const isValidTarget =
    !!dnd?.dragId &&
    !dnd.invalidTargets.has(id) &&
    dnd.draggedParentId !== id;
  const isDropTarget = isValidTarget && dnd?.overId === id;

  return (
    <>
      <div
        draggable={!!dnd}
        onClick={() => onSelect(node)}
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', id); // required for Firefox to start a drag
          dnd?.onDragStart(id);
        }}
        onDragEnter={(e) => {
          if (!isValidTarget) return;
          e.preventDefault();
          dnd?.onDragEnterNode(id);
        }}
        onDragOver={(e) => {
          if (!isValidTarget) return;
          e.preventDefault(); // allow drop
        }}
        onDrop={(e) => {
          if (!isValidTarget) return;
          e.preventDefault();
          e.stopPropagation();
          dnd?.onDropNode(id);
        }}
        onDragEnd={() => dnd?.onDragEnd()}
        className={`flex items-center justify-between gap-2 py-2 px-3 border-b border-gray-100 dark:border-gray-700 cursor-pointer ${
          isDropTarget
            ? 'bg-orange-50 dark:bg-orange-900/30 ring-2 ring-inset ring-secondary'
            : isSelected
            ? 'bg-blue-50 dark:bg-blue-900/30'
            : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
        } ${isDragging ? 'opacity-40' : ''}`}
      >
        <div
          className="flex items-center min-w-0"
          style={{ paddingLeft: depth * 22 }}
        >
          {dnd && (
            <GripVertical
              size={14}
              className="mr-1 shrink-0 text-gray-300 dark:text-gray-600"
              aria-hidden="true"
            />
          )}
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(id);
              }}
              className="mr-1 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              aria-label={expanded ? 'Collapse' : 'Expand'}
              aria-expanded={expanded}
            >
              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          ) : (
            <span className="inline-block w-4 mr-1" />
          )}
          <span
            className={`font-medium truncate ${
              isSelected
                ? 'text-blue-700 dark:text-blue-300'
                : 'text-gray-800 dark:text-gray-100'
            }`}
          >
            {node.title || '(untitled)'}
          </span>
          <span className="ml-2 text-xs text-gray-400 truncate">/{node.slug}</span>
          {hasChildren && !expanded && (
            <span className="ml-2 shrink-0 rounded-full bg-gray-100 dark:bg-gray-700 px-2 text-xs text-gray-500 dark:text-gray-300">
              {node.children.length}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(node);
          }}
          className="p-1 text-gray-400 hover:text-red-600 shrink-0"
          aria-label="Delete"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {expanded &&
        node.children.map((child) => (
          <NodeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            selectedId={selectedId}
            onSelect={onSelect}
            onDelete={onDelete}
            expandedIds={expandedIds}
            onToggleExpand={onToggleExpand}
            expandAll={expandAll}
            dnd={dnd}
          />
        ))}
    </>
  );
};

const PagesTreeView: React.FC<PagesTreeViewProps> = ({
  data,
  selectedId,
  onSelect,
  onDelete,
  onMove,
  expandAll = false,
  isLoading = false,
}) => {
  // Every node starts collapsed — the full list runs to thousands of pages.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [overRoot, setOverRoot] = useState(false);

  // The dragged node plus its descendants — a node may never be dropped into its own subtree.
  const invalidTargets = useMemo(() => {
    const set = new Set<string>();
    if (!dragId) return set;

    const childrenByParent = new Map<string, string[]>();
    data.forEach((p) => {
      const pid = normId(p.parentPageId);
      if (!pid) return;
      const list = childrenByParent.get(pid) ?? [];
      list.push(normId(p.id));
      childrenByParent.set(pid, list);
    });

    const stack = [dragId];
    while (stack.length) {
      const cur = stack.pop() as string;
      set.add(cur);
      (childrenByParent.get(cur) ?? []).forEach((c) => stack.push(c));
    }
    return set;
  }, [dragId, data]);

  const clearDrag = () => {
    setDragId(null);
    setOverId(null);
    setOverRoot(false);
  };

  const draggedParentId = useMemo(
    () => (dragId ? normId(data.find((p) => normId(p.id) === dragId)?.parentPageId) || null : null),
    [dragId, data]
  );
  const draggedIsRoot = !!dragId && !draggedParentId;

  const dnd: DndState | undefined = onMove
    ? {
        dragId,
        draggedParentId,
        overId,
        invalidTargets,
        // Defer the state update off the dragstart tick: showing the top-level banner and
        // dimming the row mutate the DOM under the drag source, which otherwise cancels the
        // drag in Chrome for nested rows (root rows don't show the banner, so they worked).
        onDragStart: (id) => {
          window.setTimeout(() => setDragId(id), 0);
        },
        onDragEnterNode: (id) => setOverId(id),
        onDropNode: (id) => {
          if (dragId && dragId !== id) onMove(dragId, id);
          clearDrag();
        },
        onDragEnd: clearDrag,
      }
    : undefined;

  const toggleExpand = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  if (isLoading) {
    return <div className="py-10 text-center text-gray-400">Loading pages…</div>;
  }

  const tree = buildPageTree(data);

  if (tree.length === 0) {
    return <div className="py-10 text-center text-gray-400">No pages found.</div>;
  }

  const expandEverything = () => {
    const all = new Set<string>();
    data.forEach((p) => {
      const pid = normId(p.parentPageId);
      if (pid) all.add(pid); // only nodes that actually have children need an entry
    });
    setExpandedIds(all);
  };

  return (
    <div className="border border-gray-100 dark:border-gray-700 rounded-md overflow-hidden">
      {!expandAll && (
        <div className="flex items-center justify-end gap-3 py-1.5 px-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
          <button
            type="button"
            onClick={expandEverything}
            className="text-xs text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={() => setExpandedIds(new Set())}
            className="text-xs text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Collapse all
          </button>
        </div>
      )}

      {/* Sticky, clearly-separated drop target for moving a page out to the top level.
          Shown only while dragging a page that currently has a parent. */}
      {dnd && dragId && !draggedIsRoot && (
        <div
          onDragEnter={(e) => {
            e.preventDefault();
            setOverRoot(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!overRoot) setOverRoot(true);
          }}
          onDragLeave={() => setOverRoot(false)}
          onDrop={(e) => {
            e.preventDefault();
            if (dragId) onMove?.(dragId, null);
            clearDrag();
          }}
          className={`sticky top-0 z-10 mb-1 flex items-center justify-center gap-2 py-3 px-3 text-sm font-medium border-2 border-dashed transition-colors ${
            overRoot
              ? 'bg-orange-100 dark:bg-orange-900/40 border-secondary text-secondary'
              : 'bg-gray-50 dark:bg-gray-800/60 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300'
          }`}
        >
          <ArrowUpToLine size={16} aria-hidden="true" />
          Drop here to move to the top level
        </div>
      )}

      {tree.map((node) => (
        <NodeRow
          key={node.id}
          node={node}
          depth={0}
          selectedId={selectedId}
          onSelect={onSelect}
          onDelete={onDelete}
          expandedIds={expandedIds}
          onToggleExpand={toggleExpand}
          expandAll={expandAll}
          dnd={dnd}
        />
      ))}
    </div>
  );
};

export default PagesTreeView;
